
import React, { useEffect, useState } from "react";
import style from "./DetailsOffer.module.css";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  MdLocalPhone,
  MdEmail,
  MdWeb,
  MdLocationOn
} from "react-icons/md";

import {
  findById,
  selectCompany
} from "../../../../../slices/company/companySlice";

import { RootState } from "../../../../../app/store";
import OneOffer from "../OneOffer/OneOffer";
import { icons } from "../../../../../stuffs/Icons";
import { searchIcon } from "../../../../../stuffs/SearchIcon";
import Spinner from "../../../../../stuffs/Spinner/Spinner";

interface RouteParams {
  offerID: string;
}

type DateValue = string | Date | null | undefined;

type CompanyPackage = "free" | "extended" | "featured" | "top3";

type PackageDates = {
  expirationDate?: DateValue;
  highlightedExpirationDate?: DateValue;
  recommendedExpirationDate?: DateValue;
};

const SITE_URL = "https://www.cartena.pl";

const DEFAULT_TITLE =
  "Cartena.pl - Katalog firm transportowych i pojazdów specjalistycznych";

const active = (date: DateValue): boolean => {
  if (!date) return false;

  const value = new Date(date).getTime();

  return Number.isFinite(value) && value > Date.now();
};

const resolvePackage = (company: PackageDates): CompanyPackage => {
  const extended = active(company.expirationDate);
  const featured = active(company.highlightedExpirationDate);
  const recommended = active(company.recommendedExpirationDate);

  if (extended && featured && recommended) return "top3";
  if (extended && featured) return "featured";
  if (extended) return "extended";

  return "free";
};

const visiblePhotoLimits: Record<CompanyPackage, number> = {
  free: 0,
  extended: 1,
  featured: 3,
  top3: 5
};

const imageUrl = (imagePath: string): string => {
  if (/^https?:\/\//i.test(imagePath)) return imagePath;

  const base = (process.env.REACT_APP_UPLOADS_URL || "").replace(/\/$/, "");

  return `${base}/${imagePath.replace(/^\/+/, "")}`;
};

const absolutPath = (website: string): string => {
  if (/^https?:\/\//i.test(website)) return website;

  return `https://${website}`;
};

const DetailsOffer: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { offerID } = useParams<RouteParams>();

  const company = useSelector(selectCompany);

  const isLoading = useSelector(
    (state: RootState) => state.companies.isLoading
  );

  const [openedPhoto, setOpenedPhoto] = useState<string | null>(null);

  // Pobieranie danych firmy
  useEffect(() => {
    if (offerID) {
      dispatch(findById(offerID));
    }
  }, [dispatch, offerID]);

  // Zamknięcie galerii po zmianie firmy
  useEffect(() => {
    setOpenedPhoto(null);
  }, [offerID]);

  // Zamykanie galerii klawiszem Escape
  useEffect(() => {
    if (!openedPhoto) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenedPhoto(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [openedPhoto]);

  // SEO - indywidualne metadane wizytówki
  useEffect(() => {
    if (!company || !offerID || String(company._id) !== offerID) {
      return;
    }

    const companyName = company.name || "Firma transportowa";
    const companyCity = company.city || "";

    const title = companyCity
      ? `${companyName} – ${companyCity} | Cartena.pl`
      : `${companyName} | Cartena.pl`;

    const cleanDescription = (company.description || "")
      .replace(/\s+/g, " ")
      .trim();

    const fallbackDescription = companyCity
      ? `Sprawdź ofertę firmy ${companyName} z miejscowości ${companyCity}. Usługi, dane kontaktowe i informacje o firmie w katalogu Cartena.pl.`
      : `Sprawdź ofertę firmy ${companyName}. Usługi, dane kontaktowe i informacje o firmie w katalogu Cartena.pl.`;

    const description = (
      cleanDescription || fallbackDescription
    ).slice(0, 155);

    const canonicalUrl = `${SITE_URL}/offer/${offerID}`;

    // Zachowujemy poprzednie metadane do przywrócenia
    const previousTitle = document.title;

    let metaDescription = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );

    const metaCreated = !metaDescription;
    const previousDescription =
      metaDescription?.getAttribute("content") ?? null;

    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }

    let canonical = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );

    const canonicalCreated = !canonical;
    const previousCanonical =
      canonical?.getAttribute("href") ?? null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }

    document.title = title;
    metaDescription.content = description;
    canonical.href = canonicalUrl;

    // Przywracanie metadanych po opuszczeniu wizytówki
    return () => {
      document.title = previousTitle || DEFAULT_TITLE;

      if (metaCreated) {
        metaDescription?.remove();
      } else if (metaDescription) {
        if (previousDescription === null) {
          metaDescription.removeAttribute("content");
        } else {
          metaDescription.content = previousDescription;
        }
      }

      if (canonicalCreated) {
        canonical?.remove();
      } else if (canonical) {
        if (previousCanonical === null) {
          canonical.removeAttribute("href");
        } else {
          canonical.setAttribute("href", previousCanonical);
        }
      }
    };
  }, [company, offerID]);

  if (isLoading) {
    return (
      <div className={style.Content}>
        <Spinner />
      </div>
    );
  }

  if (!company || String(company._id) !== offerID) {
    return (
      <div className={style.Content}>
        <section className={style.Section}>
          <h2 className={style.SectionTitle}>
            Nie znaleziono oferty
          </h2>

          <p className={style.Description}>
            Oferta nie istnieje albo nie jest już dostępna.
          </p>
        </section>

        <div className={style.Actions}>
          <button
            type="button"
            className={style.Button}
            onClick={() => history.goBack()}
          >
            Wstecz
          </button>
        </div>
      </div>
    );
  }

  const packageCode = resolvePackage(company);

  const canShowLogo = packageCode !== "free";

  const logo =
    canShowLogo && company.images?.logo
      ? imageUrl(company.images.logo)
      : null;

  const photos = (company.images?.photos || [])
    .filter(
      (photo): photo is string =>
        typeof photo === "string" && photo.length > 0
    )
    .slice(0, visiblePhotoLimits[packageCode])
    .map(imageUrl);

  return (
    <div className={style.Content}>
      <OneOffer
        name={company.name}
        shortName={company.shortName}
        website={company.website}
        email={company.email}
        phone={company.phone}
        NIP={company.NIP}
        description={company.description}
        mainField={company.mainField?.[0] || ""}
        isHighlighted={
          packageCode === "featured" || packageCode === "top3"
        }
        isRecommended={packageCode === "top3"}
        isDetailsMode
        voivodeship={company.voivodeship}
      />

      <div className={style.CompanyInfoGrid}>
        <section className={style.Section}>
          <h2 className={style.SectionTitle}>
            Dane firmy
          </h2>

          <div className={style.CompanyDataWithLogo}>
            {logo && (
              <div className={style.CompanyLogoBox}>
                <img
                  className={style.CompanyLogo}
                  src={logo}
                  alt={`Logo firmy ${company.name}`}
                  loading="lazy"
                />
              </div>
            )}

            <div className={style.CompanyData}>
              <div className={style.CompanyName}>
                {company.name}
              </div>

              <div>NIP: {company.NIP}</div>

              <div className={style.Address}>
                <MdLocationOn />

                <div>
                  <div>
                    {company.street} {company.streetNumber}
                    {company.localNumber &&
                      `/${company.localNumber}`}
                  </div>

                  <div>
                    {company.zipCode} {company.city}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={style.Section}>
          <h2 className={style.SectionTitle}>
            Kontakt
          </h2>

          <div className={style.ContactData}>
            <a
              className={style.ContactItem}
              href={`tel:${company.phone}`}
            >
              <MdLocalPhone />
              <span>{company.phone}</span>
            </a>

            <a
              className={style.ContactItem}
              href={`mailto:${company.email}`}
            >
              <MdEmail />
              <span>{company.email}</span>
            </a>

            {company.website && (
              <a
                className={style.ContactItem}
                href={absolutPath(company.website)}
                target="_blank"
                rel="noreferrer"
              >
                <MdWeb />
                <span>{company.website}</span>
              </a>
            )}
          </div>
        </section>
      </div>

      <section className={style.Section}>
        <h2 className={style.SectionTitle}>
          Usługi
        </h2>

        <div className={style.VehicleList}>
          {(company.mainField || []).map((field) => {
            const icon = icons.find(
              (item) => item.routing === field
            );

            return (
              <div
                className={style.VehicleItem}
                key={field}
                title={icon?.title || field}
              >
                <div className={style.VehicleIcon}>
                  {icon ? icon.svg : searchIcon[0].svg}
                </div>

                <span className={style.VehicleName}>
                  {icon?.title || field}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className={style.Section}>
        <h2 className={style.SectionTitle}>
          Opis działalności
        </h2>

        <p className={style.Description}>
          {company.description || "Brak opisu działalności."}
        </p>
      </section>

      {photos.length > 0 && (
        <section className={style.Section}>
          <h2 className={style.SectionTitle}>
            Galeria zdjęć
          </h2>

          <div className={style.PhotoGrid}>
            {photos.map((photo, index) => (
              <button
                type="button"
                key={`${photo}-${index}`}
                className={style.PhotoButton}
                onClick={() => setOpenedPhoto(photo)}
                aria-label={`Powiększ zdjęcie ${
                  index + 1
                } firmy ${company.name}`}
              >
                <img
                  className={style.PhotoImage}
                  src={photo}
                  alt={`${company.name} – zdjęcie ${index + 1}`}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      <div className={style.Actions}>
        <button
          type="button"
          className={style.Button}
          onClick={() => history.goBack()}
        >
          Wstecz
        </button>
      </div>

      {openedPhoto && (
        <div
          className={style.PhotoOverlay}
          role="presentation"
          onClick={() => setOpenedPhoto(null)}
        >
          <div
            className={style.PhotoDialog}
            role="dialog"
            aria-modal="true"
            aria-label="Powiększone zdjęcie"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={style.PhotoClose}
              onClick={() => setOpenedPhoto(null)}
              aria-label="Zamknij zdjęcie"
            >
              ×
            </button>

            <img
              className={style.PhotoLarge}
              src={openedPhoto}
              alt={`Zdjęcie firmy ${company.name} w powiększeniu`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailsOffer;
